import { z } from "zod";
import { IdempotencyKeySchema, IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
export const AgentExecutionModeSchema = z.enum(["shadow", "draft_only", "approved_execution"]);
export const RolloutStageSchema = z.enum(["none", "shadow", "draft_only", "approved_execution"]);
export const RolloutScopeSchema = z.enum([
    "encounter_plan",
    "referral",
    "laboratory_order",
    "imaging_order",
    "discharge",
]);
export const RolloutPolicyReferenceSchema = z
    .object({
    policyId: IdSchema,
    policyVersion: z.number().int().positive(),
    policyHash: Sha256Schema,
    scope: RolloutScopeSchema,
    mode: AgentExecutionModeSchema,
    modelRoutingPolicyVersion: z.number().int().positive(),
    modelRoutingPolicyHash: Sha256Schema,
    activatedAt: IsoDateTimeSchema,
})
    .strict();
export const RolloutPolicySchema = RolloutPolicyReferenceSchema.extend({
    tenantId: IdSchema,
    siteId: IdSchema,
    previousStage: RolloutStageSchema,
    validationSuiteId: IdSchema,
    validationSuiteHash: Sha256Schema,
    transitionRequestId: IdSchema,
    activatedBy: IdSchema,
    contentHash: Sha256Schema,
}).strict();
export const RolloutValidationCaseSchema = z.enum([
    "complete_fast_path",
    "complex_physician_deep_path",
    "missing_required_fact",
    "encounter_context_changed",
    "tampered_exact_hash",
    "kill_switch_before_dispatch",
]);
export const RolloutValidationOutcomeSchema = z.enum(["passed", "blocked_as_expected", "failed"]);
export const RolloutValidationCaseResultSchema = z
    .object({
    case: RolloutValidationCaseSchema,
    mode: AgentExecutionModeSchema,
    runId: IdSchema,
    outcome: RolloutValidationOutcomeSchema,
    recommendationLeakCount: z.number().int().nonnegative(),
    executionArtifactCount: z.number().int().nonnegative(),
    blockingSafetyFailureCount: z.number().int().nonnegative(),
    exactExecutionSucceeded: z.boolean(),
    evidenceHash: Sha256Schema,
})
    .strict();
export const RolloutValidationSuiteSchema = z
    .object({
    suiteId: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    scope: RolloutScopeSchema,
    targetMode: AgentExecutionModeSchema,
    modelRoutingPolicyVersion: z.number().int().positive(),
    modelRoutingPolicyHash: Sha256Schema,
    cases: z.array(RolloutValidationCaseResultSchema).length(18),
    recordedBy: IdSchema,
    recordedAt: IsoDateTimeSchema,
    contentHash: Sha256Schema,
})
    .strict()
    .superRefine((suite, context) => {
    const expectedModes = AgentExecutionModeSchema.options;
    const expectedCases = RolloutValidationCaseSchema.options;
    const bindings = new Set(suite.cases.map((item) => `${item.mode}:${item.case}`));
    for (const mode of expectedModes) {
        for (const validationCase of expectedCases) {
            if (!bindings.has(`${mode}:${validationCase}`)) {
                context.addIssue({
                    code: "custom",
                    path: ["cases"],
                    message: `Validation suite is missing ${mode}:${validationCase}`,
                });
            }
        }
    }
    if (bindings.size !== suite.cases.length) {
        context.addIssue({
            code: "custom",
            path: ["cases"],
            message: "Validation suite mode/case bindings must be unique",
        });
    }
});
export const RolloutValidationSuiteReceiptSchema = z
    .object({
    suiteId: IdSchema,
    contentHash: Sha256Schema,
    passed: z.boolean(),
    caseCount: z.literal(18),
})
    .strict();
export const RolloutTransitionProposalSchema = z
    .object({
    requestId: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    scope: RolloutScopeSchema,
    fromStage: RolloutStageSchema,
    toStage: AgentExecutionModeSchema,
    expectedActivePolicyVersion: z.number().int().nonnegative(),
    validationSuiteId: IdSchema,
    validationSuiteHash: Sha256Schema,
    modelRoutingPolicyVersion: z.number().int().positive(),
    modelRoutingPolicyHash: Sha256Schema,
    proposedBy: IdSchema,
    attestation: z.string().min(20).max(2_000),
    idempotencyKey: IdempotencyKeySchema,
    contentHash: Sha256Schema,
})
    .strict();
export const RolloutTransitionProposalReceiptSchema = z
    .object({
    requestId: IdSchema,
    fromStage: RolloutStageSchema,
    toStage: AgentExecutionModeSchema,
    expectedActivePolicyVersion: z.number().int().nonnegative(),
    validationSuiteId: IdSchema,
    validationSuiteHash: Sha256Schema,
    proposedBy: IdSchema,
    contentHash: Sha256Schema,
    status: z.literal("pending_review"),
})
    .strict();
export const RolloutTransitionApprovalSchema = z
    .object({
    approvalId: IdSchema,
    requestId: IdSchema,
    expectedRequestHash: Sha256Schema,
    validationSuiteId: IdSchema,
    validationSuiteHash: Sha256Schema,
    modelRoutingPolicyVersion: z.number().int().positive(),
    modelRoutingPolicyHash: Sha256Schema,
    reviewedBy: IdSchema,
    attestation: z.string().min(20).max(2_000),
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const RolloutTransitionReceiptSchema = z
    .object({
    requestId: IdSchema,
    approvalId: IdSchema,
    policy: RolloutPolicyReferenceSchema,
    proposedBy: IdSchema,
    approvedBy: IdSchema,
})
    .strict();
export const RolloutGovernanceStateSchema = z
    .object({
    scope: RolloutScopeSchema,
    activePolicy: RolloutPolicyReferenceSchema.nullable(),
    pendingTransitions: z.array(RolloutTransitionProposalReceiptSchema),
})
    .strict();
export const DraftReviewDispositionSchema = z.enum([
    "unchanged",
    "minor_edit",
    "major_edit",
    "rejected",
]);
export const DraftActionFeedbackSchema = z
    .object({
    actionId: IdSchema,
    expectedActionVersion: z.number().int().positive(),
    exactPreviewHash: Sha256Schema,
    disposition: DraftReviewDispositionSchema,
    correctedPayload: z.record(z.string(), z.unknown()).optional(),
    reason: z.string().min(3).max(2_000).optional(),
})
    .strict()
    .superRefine((feedback, context) => {
    const edited = feedback.disposition === "minor_edit" || feedback.disposition === "major_edit";
    if (edited && feedback.correctedPayload === undefined) {
        context.addIssue({
            code: "custom",
            path: ["correctedPayload"],
            message: "Edited draft feedback requires a corrected non-executable payload",
        });
    }
    if (!edited && feedback.correctedPayload !== undefined) {
        context.addIssue({
            code: "custom",
            path: ["correctedPayload"],
            message: "Only edited draft feedback may include a corrected payload",
        });
    }
    if ((feedback.disposition === "major_edit" || feedback.disposition === "rejected") &&
        feedback.reason === undefined) {
        context.addIssue({
            code: "custom",
            path: ["reason"],
            message: "Major edits and rejections require a reason",
        });
    }
});
export const DraftReviewFeedbackSchema = z
    .object({
    feedbackId: IdSchema,
    runId: IdSchema,
    bundleId: IdSchema,
    expectedBundleVersion: z.number().int().positive(),
    expectedBundleHash: Sha256Schema,
    actionFeedback: z.array(DraftActionFeedbackSchema).min(1).max(50),
    idempotencyKey: IdempotencyKeySchema,
})
    .strict()
    .superRefine((feedback, context) => {
    const ids = feedback.actionFeedback.map((item) => item.actionId);
    if (new Set(ids).size !== ids.length) {
        context.addIssue({
            code: "custom",
            path: ["actionFeedback"],
            message: "Draft feedback action identifiers must be unique",
        });
    }
});
export const DraftReviewFeedbackReceiptSchema = z
    .object({
    feedbackId: IdSchema,
    runId: IdSchema,
    bundleId: IdSchema,
    recordedBy: IdSchema,
    recordedAt: IsoDateTimeSchema,
    contentHash: Sha256Schema,
    executable: z.literal(false),
})
    .strict();
//# sourceMappingURL=rollout-governance.js.map